// 🌱 Comprehensive Grass Characteristics Database
// Values cross-checked against university extension sources (PSU, Clemson, UGA,
// UF/IFAS, UMN, MU, CSU). Per-characteristic citations live in data/grass-citations.ts;
// see GRASS_CHARACTERISTICS_LAST_VERIFIED for the freshness date.

import { GrassCharacteristics } from '@/lib/types/recommendation-engine'
import { CARPETGRASS } from './carpetgrass'

/** ISO date the GRASS_CHARACTERISTICS values were last cross-checked against
 *  extension sources. Surfaced to users and used for a staleness check. */
export const GRASS_CHARACTERISTICS_LAST_VERIFIED = '2026-05-06'

export const GRASS_CHARACTERISTICS: Record<string, GrassCharacteristics> = {
  'Kentucky Bluegrass': {
    name: 'Kentucky Bluegrass',
    photoIdCues: [
      'Dark green with a blue tint; densest, most uniform carpet of the cool-season grasses',
      'Fine, soft blades noticeably narrower than tall fescue',
      'Close-up: blade tips are boat-shaped (keeled like a canoe bow)',
      'No visible runners at edges; fills in as a seamless mat',
    ],
    seedhead: 'Open, pyramid-shaped branched panicle; rarely seen in mowed lawns',
    dormancyAppearance: 'Goes uniformly straw-tan in extended summer heat/drought and browns after hard fall frost; greens up mid-spring',
    lookalikes: [
      { grass: 'Tall Fescue', tell: 'Tall fescue has much wider, coarser blades with visible ribbing; KBG is finer with a boat-shaped tip' },
      { grass: 'Fine Fescue', tell: 'Fine fescue blades are even narrower — needle-like and often folded — while KBG blades lie flat with a boat tip' },
      { grass: 'Perennial Ryegrass', tell: 'Ryegrass has a glossy, shiny back of the blade; KBG is duller with a visible boat-shaped tip' },
    ],
    bladeWidth: 'Fine (2-4mm)',
    texture: 'Smooth',
    growthHabit: 'Spreading (rhizomes)',
    bladeShape: 'Boat-shaped tip, V-shaped cross-section',
    color: 'Dark green with blue tint',
    density: 'Very dense, uniform carpet',
    rootSystem: 'Shallow rhizomes',
    season: 'Cool',
    wateringNeeds: {
      weeklyRequirements: {
        spring: 1.0,
        summer: 1.5,
        fall: 1.0,
        winter: 0.5
      },
      rootDepth: 18,
      droughtTolerance: 'Medium',
      irrigationFrequency: {
        spring: '2-3 times per week',
        summer: '3-4 times per week',
        fall: '2-3 times per week',
        winter: '1-2 times per week'
      },
      specialConsiderations: [
        'High water requirements',
        'Shallow root system',
        'Needs consistent moisture'
      ]
    },
    droughtTolerance: 'Medium',
    shadeTolerance: 'Low',
    trafficTolerance: 'High',
    diseaseResistance: 'Medium',
    mowingHeight: '2.0-3.5 inches',
    mowingDetails: {
      optimal: '2.0-3.5 inches',
      minimum: '1.5 inches',
      maximum: '4 inches',
      frequency: {
        growingSeason: 'Weekly',
        peakGrowth: 'Every 5-7 days',
        dormantSeason: 'Bi-weekly or as needed'
      },
      tips: [
        'Never remove more than 1/3 of grass blade at once',
        'Keep blades sharp for clean cuts',
        'Raise height during summer stress periods',
        'Lower gradually in fall for winter preparation'
      ]
    },
    soilPreference: 'Well-drained, neutral pH',
    pHRange: '6.0-7.0',
    maintenanceLevel: 'High',
    coldTolerance: 'Very High (winter dormant after hard freeze)',
    establishmentTime: '14-28 days',
    peakGrowth: 'March-May, September-November',
    dormant: 'December-February (winter); summer dormancy possible under prolonged heat/drought without irrigation',
    fertilization: 'Heavy (4x/year)',
    fertilizationNeeds: {
      annualNPK: {
        nitrogen: 4.0,
        phosphorus: 1.0,
        potassium: 3.0
      },
      seasonalSplit: {
        spring: 30,
        summer: 0,
        fall: 70,
        winter: 0
      },
      timing: {
        firstApplication: 'When soil temperature reaches 55°F',
        lastApplication: '6-8 weeks before first frost',
        frequency: 'Every 6-8 weeks during growing season'
      }
    },
    aerationTiming: {
      optimalSeasons: ['spring', 'fall'],
      soilTemperature: 'When soil reaches 55-65°F',
      frequency: 'Once per year for established lawns',
      depth: '2-3 inches',
      timing: {
        spring: 'Early spring before growth begins',
        fall: 'Early fall after summer stress'
      },
      considerations: [
        'Aerate when soil is moist but not wet',
        'Avoid aeration during drought stress',
        'Follow with overseeding for best results',
        'Allow 2-3 weeks recovery before heavy use'
      ]
    },
    overseeding: 'Early fall',
    seedingRate: {
      newLbsPer1000Min: 2,
      newLbsPer1000Max: 3,
      overseedLbsPer1000Min: 1,
      overseedLbsPer1000Max: 1.5,
    },
  },

  'Perennial Ryegrass': {
    name: 'Perennial Ryegrass',
    photoIdCues: [
      'Medium-dark green with a distinct glossy shine on the back of the blade',
      'Fine-to-medium blades growing in bunches, not a spreading mat',
      'Close-up: prominent veins on the upper blade surface, shiny smooth underside',
      'Often the fastest-germinating patchwork in overseeded lawns',
    ],
    seedhead: 'Slender flattened spike with spikelets set edge-on to the stem; occasionally seen between mowings',
    dormancyAppearance: 'Browns out in prolonged summer heat/drought; stays green through mild winters, browns in hard-freeze climates',
    lookalikes: [
      { grass: 'Tall Fescue', tell: 'Tall fescue blades are wider and coarser; ryegrass is finer with a glossier blade back' },
      { grass: 'Kentucky Bluegrass', tell: 'KBG has a boat-shaped blade tip and dull finish; ryegrass tips are pointed and the blade back is glossy' },
      { grass: 'Annual Ryegrass', tell: 'Annual ryegrass is lighter, brighter green with coarser blades and a thinner, clumpier stand' },
    ],
    bladeWidth: 'Fine (2-4mm)',
    texture: 'Smooth, glossy',
    growthHabit: 'Bunch-type (clumping)',
    bladeShape: 'Shiny, pointed tips',
    color: 'Dark green, glossy',
    density: 'Dense within clumps',
    rootSystem: 'Fibrous roots',
    season: 'Cool',
    droughtTolerance: 'Low',
    shadeTolerance: 'Medium',
    trafficTolerance: 'High',
    diseaseResistance: 'Medium',
    mowingHeight: '1.5-2.5 inches',
    mowingDetails: {
      optimal: '1.5-2.5 inches',
      minimum: '1.5 inches',
      maximum: '3.5 inches',
      frequency: {
        growingSeason: 'Weekly',
        peakGrowth: 'Every 5-7 days',
        dormantSeason: 'Bi-weekly or as needed'
      },
      tips: [
        'Tolerates lower mowing heights well',
        'Maintain consistent height for best appearance',
        'Cut when grass reaches 4-4.5 inches',
        'Bag clippings if excessive growth'
      ]
    },
    soilPreference: 'Well-drained, fertile',
    pHRange: '6.0-7.0',
    maintenanceLevel: 'Medium',
    coldTolerance: 'Medium (susceptible to winter kill)',
    establishmentTime: '5-10 days',
    peakGrowth: 'March-May, September-November',
    dormant: 'December-February',
    fertilization: 'Moderate (3x/year)',
    fertilizationNeeds: {
      annualNPK: {
        nitrogen: 3.0,
        phosphorus: 1.5,
        potassium: 2.5
      },
      seasonalSplit: {
        spring: 25,
        summer: 5,
        fall: 70,
        winter: 0
      },
      timing: {
        firstApplication: 'Early spring when growth begins',
        lastApplication: 'Late fall (early-to-mid November), applied near the final mow while still green, at 0.5-1.0 lb N/1,000 sq ft',
        frequency: 'Light spring feeding; fall-focused with September the key feed'
      }
    },
    aerationTiming: {
      optimalSeasons: ['fall'],
      soilTemperature: 'When soil reaches 55-60°F',
      frequency: 'Once per year, fall preferred',
      depth: '2-3 inches',
      timing: {
        spring: 'Late spring if needed (not preferred)',
        fall: 'Early fall for best results'
      },
      considerations: [
        'Fall aeration preferred over spring',
        'Less frequent aeration needed than bluegrass',
        'Aerate before overseeding for better seed contact',
        'Avoid aeration during heat stress'
      ]
    },
    wateringNeeds: {
      weeklyRequirements: {
        spring: 1.0,
        summer: 1.2,
        fall: 1.0,
        winter: 0.3
      },
      rootDepth: 12,
      droughtTolerance: 'Low',
      irrigationFrequency: {
        spring: '2-3 times per week',
        summer: '3-4 times per week',
        fall: '2-3 times per week',
        winter: '1 time per week (if needed)'
      },
      specialConsiderations: [
        'Less drought tolerant than other cool-season grasses',
        'Requires more frequent watering during heat stress',
        'Shallow root system needs consistent moisture'
      ]
    },
    overseeding: 'Early fall',
    seedingRate: {
      newLbsPer1000Min: 6,
      newLbsPer1000Max: 8,
      overseedLbsPer1000Min: 3,
      overseedLbsPer1000Max: 4,
    },
  },

  'Tall Fescue': {
    name: 'Tall Fescue',
    photoIdCues: [
      'Widest, coarsest blades of the common cool-season lawns, with prominent ribbing/veins on the upper surface',
      'Grows in bunches — thin lawns show distinct coarse clumps',
      'Medium-to-dark green; keeps color deep into summer when neighbors brown',
      'No runners at lawn edges; spreads only by tillering',
    ],
    seedhead: 'Compact, slightly nodding branched panicle; rarely seen in mowed lawns',
    dormancyAppearance: 'Stays green longer than other cool-season grasses; only extreme extended drought browns it, and clumps brown unevenly',
    lookalikes: [
      { grass: 'Perennial Ryegrass', tell: 'Ryegrass is finer with a glossy blade back; tall fescue is wider, duller, and visibly ribbed on top' },
      { grass: 'Kentucky Bluegrass', tell: 'KBG is much finer with boat-shaped tips and a uniform carpet; tall fescue is coarse and clumpy' },
      { grass: 'Mixed Cool-Season Blend', tell: 'A pure tall fescue stand is uniformly coarse; a blend shows patches of finer texture mixed among coarse clumps' },
    ],
    bladeWidth: 'Wide (3-8mm)',
    texture: 'Coarse, rough edges',
    growthHabit: 'Clumping/bunch-type',
    bladeShape: 'Flat with prominent midrib',
    color: 'Deep green to blue-green',
    density: 'Less dense, clumps visible',
    rootSystem: 'Deep fibrous roots',
    season: 'Cool',
    droughtTolerance: 'High',
    shadeTolerance: 'Medium',
    trafficTolerance: 'High',
    diseaseResistance: 'High',
    mowingHeight: '3-4 inches',
    mowingDetails: {
      optimal: '3.0-4.0 inches',
      minimum: '2.5 inches',
      maximum: '4.5 inches',
      frequency: {
        growingSeason: 'Weekly',
        peakGrowth: 'Every 5-7 days',
        dormantSeason: 'Bi-weekly or as needed'
      },
      tips: [
        'Never remove more than 1/3 of blade at once',
        'Keep at 3.5-4 inches during summer heat for drought tolerance',
        'Sharpen blades regularly - tall fescue tears easily with dull blades',
        'Raise height in shade areas to 4 inches'
      ]
    },
    soilPreference: 'Tolerates poor soil',
    pHRange: '5.5-6.5',
    maintenanceLevel: 'Medium',
    coldTolerance: 'High (growth slows below ~50°F but stays green; least winter-hardy common cool-season grass)',
    establishmentTime: '14-21 days',
    peakGrowth: 'March-May, September-November',
    dormant: 'December-February (semi-dormant; stays green through winter in the South - not true dormancy)',
    fertilization: 'Light (2-3x/year)',
    fertilizationNeeds: {
      annualNPK: {
        nitrogen: 3.0,
        phosphorus: 1.0,
        potassium: 2.0
      },
      seasonalSplit: {
        spring: 30,
        summer: 0,
        fall: 70,
        winter: 0
      },
      timing: {
        firstApplication: 'Early spring when growth begins',
        lastApplication: 'Late fall (November) for root reserves and winter hardiness',
        frequency: 'Every 8-10 weeks during growing season'
      }
    },
    wateringNeeds: {
      weeklyRequirements: {
        spring: 0.8,
        summer: 1.0,
        fall: 0.8,
        winter: 0.3
      },
      rootDepth: 30,
      droughtTolerance: 'High',
      irrigationFrequency: {
        spring: '1-2 times per week',
        summer: '2-3 times per week',
        fall: '1-2 times per week',
        winter: '1 time per week (if needed)'
      },
      specialConsiderations: [
        'Most drought tolerant cool-season grass',
        'Deep roots (2-3 feet) allow for less frequent watering',
        'Can survive extended periods with minimal irrigation'
      ]
    },
    aerationTiming: {
      optimalSeasons: ['fall'],
      soilTemperature: 'When soil reaches 55-65°F',
      frequency: 'Once per year, fall preferred',
      depth: '2-3 inches',
      timing: {
        spring: 'Early spring if needed (not preferred)',
        fall: 'Early fall for best results'
      },
      considerations: [
        'Deep root system benefits greatly from aeration',
        'Fall aeration preferred for cool-season recovery',
        'Combine with overseeding for thicker turf',
        'Avoid aeration during summer heat stress'
      ]
    },
    overseeding: 'Early fall',
    seedingRate: {
      newLbsPer1000Min: 6,
      newLbsPer1000Max: 8,
      overseedLbsPer1000Min: 3,
      overseedLbsPer1000Max: 4,
    },
  },

  'Fine Fescue': {
    name: 'Fine Fescue',
    photoIdCues: [
      'Extremely fine, needle-like blades — often folded or wiry, the narrowest of any lawn grass',
      'Medium-green, sometimes with a gray or blue cast; soft, hair-like look',
      'Thrives in shaded areas where other grasses thin out',
      'Fine-bladed clumps can give the surface a wispy, feathery texture',
    ],
    seedhead: 'Small narrow panicle; rarely seen in mowed lawns',
    dormancyAppearance: 'Goes patchy tan in summer heat and drought, especially in full sun; recovers in cool fall weather',
    lookalikes: [
      { grass: 'Kentucky Bluegrass', tell: 'KBG blades are wider and flat with a boat-shaped tip; fine fescue blades are needle-thin and often folded' },
      { grass: 'Perennial Ryegrass', tell: 'Ryegrass blades are broader with a glossy back; fine fescue looks wispy and hair-like' },
    ],
    bladeWidth: 'Very Fine (1-2mm)',
    texture: 'Needle-like',
    growthHabit: 'Bunch-type',
    bladeShape: 'Very narrow, needle-like',
    color: 'Light green to gray-green',
    density: 'Fine, less dense',
    rootSystem: 'Fibrous, moderate depth',
    season: 'Cool',
    wateringNeeds: {
      weeklyRequirements: {
        spring: 0.8,
        summer: 0.5,
        fall: 0.8,
        winter: 0.3
      },
      rootDepth: 12,
      droughtTolerance: 'High',
      irrigationFrequency: {
        spring: '1-2 times per week',
        summer: '1 time per week or less; water deeply',
        fall: '1-2 times per week',
        winter: '1 time per week (if needed)'
      },
      specialConsiderations: [
        'Good drought avoidance through dormancy',
        'Water deeply but infrequently; fine fescue prefers drier soil and tolerates long dry spells',
        'Low water requirements overall'
      ]
    },
    droughtTolerance: 'High',
    shadeTolerance: 'High',
    trafficTolerance: 'Low',
    diseaseResistance: 'Medium',
    mowingHeight: '2.5-4 inches',
    mowingDetails: {
      optimal: '2.5-4.0 inches',
      minimum: '2.0 inches',
      maximum: '4.5 inches',
      frequency: {
        growingSeason: 'Every 7-10 days',
        peakGrowth: 'Weekly',
        dormantSeason: 'As needed or not at all'
      },
      tips: [
        'Fine fescue tolerates less frequent mowing than other cool-season grasses',
        'Raise to 3.5-4 inches in summer and shade',
        'Can be left unmowed in naturalized areas',
        'Avoid scalping - recovers slowly from damage'
      ]
    },
    soilPreference: 'Sandy, well-drained',
    pHRange: '5.5-7.0',
    maintenanceLevel: 'Very Low',
    coldTolerance: 'Very High (dormant below 45°F)',
    establishmentTime: '5-12 days',
    peakGrowth: 'March-May, September-November',
    dormant: 'December-February',
    fertilization: 'Light (1-2x/year)',
    fertilizationNeeds: {
      annualNPK: {
        nitrogen: 1.5,
        phosphorus: 0.5,
        potassium: 1.0
      },
      seasonalSplit: {
        spring: 40,
        summer: 0,
        fall: 60,
        winter: 0
      },
      timing: {
        firstApplication: 'Late spring (May-June) when growth is active',
        lastApplication: 'Early fall',
        frequency: 'Once or twice during growing season'
      }
    },
    aerationTiming: {
      optimalSeasons: ['fall'],
      soilTemperature: 'When soil reaches 55-60°F',
      frequency: 'Once every 1-2 years',
      depth: '2-3 inches',
      timing: {
        spring: 'Early spring if needed (not preferred)',
        fall: 'Early fall for best results'
      },
      considerations: [
        'Low-maintenance grass needs less frequent aeration',
        'Fall aeration preferred for cool-season recovery',
        'Moderate root depth; standard core depth is fine',
        'Avoid aeration in shade-stressed areas'
      ]
    },
    overseeding: 'Early fall',
    seedingRate: {
      newLbsPer1000Min: 4,
      newLbsPer1000Max: 5,
      overseedLbsPer1000Min: 2,
      overseedLbsPer1000Max: 3,
    },
  },

  'Bermudagrass': {
    name: 'Bermudagrass',
    photoIdCues: [
      'Fine gray-green to deep green blades forming a dense, low, tight mat',
      'Wiry stolons (runners) clearly visible creeping over sidewalks, edges, and bare spots',
      'Fine texture with short internodes; scalped areas show purplish stems',
      'Seedheads with 3-7 finger-like spikes often present even in mowed lawns',
    ],
    seedhead: '3-7 slender finger-like spikes radiating from a single point (bird-foot shape); common even in mowed lawns',
    dormancyAppearance: 'Uniform straw-brown mat all winter once soil drops below ~55°F; among the first to brown in fall and greens up in late spring',
    lookalikes: [
      { grass: 'Kikuyugrass', tell: 'Kikuyu is lighter apple-green with thicker, flattened stems and white thread-like stigma filaments; bermuda is finer and grayer-green with bird-foot seedheads' },
      { grass: 'Zoysiagrass', tell: 'Zoysia feels stiff and bristly with slower, less obvious runners; bermuda shows aggressive wiry stolons and bird-foot seedheads' },
      { grass: 'Buffalograss', tell: 'Buffalograss is grayer sage-green with curly blades and a thin open stand; bermuda is denser and greener' },
    ],
    bladeWidth: 'Fine (1-3mm)',
    texture: 'Wiry',
    growthHabit: 'Aggressive spreading (rhizomes + stolons)',
    bladeShape: 'Fine, wiry, pointed',
    color: 'Medium green to dark green',
    density: 'Very dense, aggressive',
    rootSystem: 'Deep rhizomes + surface stolons',
    season: 'Warm',
    wateringNeeds: {
      weeklyRequirements: {
        spring: 0.8,
        summer: 1.0,
        fall: 0.8,
        winter: 0.0
      },
      rootDepth: 72,
      droughtTolerance: 'High',
      irrigationFrequency: {
        spring: '2-3 times per week',
        summer: '2-3 times per week',
        fall: '2-3 times per week',
        winter: 'Not needed (dormant)'
      },
      specialConsiderations: [
        'Dormant in winter, no irrigation needed',
        'Heat and drought tolerant',
        'Deep roots (6+ feet) reduce irrigation needs'
      ]
    },
    droughtTolerance: 'High',
    shadeTolerance: 'Low',
    trafficTolerance: 'Very High',
    diseaseResistance: 'High',
    mowingHeight: '1-2 inches',
    mowingDetails: {
      optimal: '1-2 inches',
      minimum: '1.0 inches',
      maximum: '2.5 inches',
      frequency: {
        growingSeason: 'Every 5-7 days',
        peakGrowth: 'Every 3-5 days',
        dormantSeason: 'Not needed (dormant)'
      },
      tips: [
        'A sharp reel or rotary blade keeps the cut clean at 1 to 2 inches',
        'Frequent mowing encourages lateral growth',
        'Raise height during drought stress',
        'Scalping in spring removes dead grass'
      ]
    },
    soilPreference: 'Well-drained, tolerates poor soil',
    pHRange: '5.8-6.5',
    maintenanceLevel: 'High',
    coldTolerance: 'Low (dormant below 50°F, damage below 25°F)',
    establishmentTime: '14-21 days',
    peakGrowth: 'May-September',
    dormant: 'November-March',
    fertilization: 'Heavy (4-5x/year)',
    fertilizationNeeds: {
      annualNPK: {
        nitrogen: 3.5,
        phosphorus: 1.0,
        potassium: 3.0
      },
      seasonalSplit: {
        spring: 20,
        summer: 80,
        fall: 0,
        winter: 0
      },
      timing: {
        firstApplication: 'When soil temperature reaches 65°F',
        lastApplication: 'Late summer, before mid-August (do not apply nitrogen in fall - late N promotes spring dead spot)',
        frequency: 'Every 4-6 weeks during growing season'
      }
    },
    aerationTiming: {
      optimalSeasons: ['spring'],
      soilTemperature: 'When soil reaches 65-70°F',
      frequency: 'Once per year in spring',
      depth: '1-2 inches',
      timing: {
        spring: 'Late spring after dormancy breaks',
        fall: 'Not recommended (preparing for dormancy)'
      },
      considerations: [
        'Spring aeration only - avoid fall aeration',
        'Aerate after spring green-up begins',
        'Can tolerate more frequent aeration than cool-season grasses',
        'Combine with dethatching for best results'
      ]
    },
    overseeding: 'Late spring',
    seedingRate: {
      newLbsPer1000Min: 1,
      newLbsPer1000Max: 2,
      overseedLbsPer1000Min: 0.5,
      overseedLbsPer1000Max: 1,
    },
  },

  'Zoysiagrass': {
    name: 'Zoysiagrass',
    photoIdCues: [
      'Very dense, carpet-like turf that feels stiff and bristly underfoot',
      'Fine-to-medium blades, medium green, growing noticeably slower than bermuda',
      'Edges creep slowly; runners present but far less rampant than bermuda',
      'Blades stand upright and hold a crisp, manicured look between mowings',
    ],
    seedhead: 'Short, single spike-like raceme just above the canopy; rarely seen in mowed lawns',
    dormancyAppearance: 'Uniform straw-beige mat in winter; one of the first warm-season grasses to brown in fall and among the last to green up',
    lookalikes: [
      { grass: 'Bermudagrass', tell: 'Bermuda spreads with visible wiry stolons and bird-foot seedheads; zoysia is stiffer, denser, and slower-creeping' },
      { grass: 'Centipede', tell: 'Centipede is a paler apple-green with blunt blade tips; zoysia is deeper green with stiff, pointed blades' },
    ],
    bladeWidth: 'Medium (3-5mm)',
    texture: 'Stiff',
    growthHabit: 'Slow spreading (rhizomes + stolons)',
    bladeShape: 'Stiff, medium-width',
    color: 'Dark green',
    density: 'Very dense, carpet-like',
    rootSystem: 'Rhizomes + stolons',
    season: 'Warm',
    wateringNeeds: {
      weeklyRequirements: {
        spring: 0.8,
        summer: 1.0,
        fall: 0.8,
        winter: 0.0
      },
      rootDepth: 36,
      droughtTolerance: 'High',
      irrigationFrequency: {
        spring: '1-2 times per week',
        summer: '2-3 times per week',
        fall: '1-2 times per week',
        winter: 'Not needed (dormant)'
      },
      specialConsiderations: [
        'Deep root system reduces watering needs',
        'Dormant in winter, no irrigation needed',
        'Very drought tolerant once established'
      ]
    },
    droughtTolerance: 'High',
    shadeTolerance: 'Medium',
    trafficTolerance: 'High',
    diseaseResistance: 'High',
    mowingHeight: '1-2.5 inches',
    mowingDetails: {
      optimal: '1.0-2.5 inches',
      minimum: '0.75 inches',
      maximum: '3.0 inches',
      frequency: {
        growingSeason: 'Every 5-7 days',
        peakGrowth: 'Every 4-5 days',
        dormantSeason: 'Not needed (dormant)'
      },
      tips: [
        'Zoysia grows slowly but forms very dense turf',
        'Use a reel mower for heights below 1.5 inches',
        'Scalp in early spring before green-up to remove dead material',
        'Thatch builds quickly - dethatch when it exceeds 0.5 inches'
      ]
    },
    soilPreference: 'Wide range, good drainage',
    pHRange: '6.0-7.0',
    maintenanceLevel: 'Medium',
    coldTolerance: 'High (one of the most cold-tolerant warm-season grasses; more cold-hardy than bermuda or St. Augustine; dormant below ~55°F)',
    establishmentTime: '21-28 days',
    peakGrowth: 'May-September',
    dormant: 'November-March',
    fertilization: 'Moderate (2-3x/year)',
    fertilizationNeeds: {
      annualNPK: {
        nitrogen: 2.0,
        phosphorus: 1.0,
        potassium: 2.0
      },
      seasonalSplit: {
        spring: 25,
        summer: 75,
        fall: 0,
        winter: 0
      },
      timing: {
        firstApplication: 'Late spring after full green-up',
        lastApplication: 'Late summer (last nitrogen by mid-August; ~6 weeks before expected dormancy)',
        frequency: 'Every 6-8 weeks during growing season'
      }
    },
    aerationTiming: {
      optimalSeasons: ['spring'],
      soilTemperature: 'When soil reaches 70-80°F',
      frequency: 'Once per year in late spring',
      depth: '1-2 inches',
      timing: {
        spring: 'Late spring after full green-up',
        fall: 'Not recommended (approaching dormancy)'
      },
      considerations: [
        'Spring aeration only; avoid fall aeration',
        'Dense turf benefits greatly from annual aeration',
        'Slow growth means longer recovery time',
        'Aerate after spring green-up is complete'
      ]
    },
    overseeding: 'Late spring',
    seedingRate: {
      newLbsPer1000Min: 1,
      newLbsPer1000Max: 2,
      overseedLbsPer1000Min: 0.5,
      overseedLbsPer1000Max: 1,
    },
  },

  'St. Augustinegrass': {
    name: 'St. Augustinegrass',
    photoIdCues: [
      'Very wide, flat, coarse blades with rounded (boat-bow) tips — the widest common lawn grass',
      'Blue-green color and a spongy, thick-feeling turf',
      'Thick, flattened above-ground stolons plainly visible at edges and in thin spots',
      'Coarse texture obvious even in photos taken standing up',
    ],
    seedhead: 'Single thick, spike-like raceme; occasionally seen between mowings',
    dormancyAppearance: 'Turns brown-tan after frost in cooler parts of its range; stays green year-round in frost-free coastal and south Florida lawns',
    lookalikes: [
      { grass: 'Centipede', tell: 'St. Augustine blades are much wider and blue-green with thick flattened stolons; centipede is narrower, apple-green, with slender stolons' },
      { grass: 'Zoysiagrass', tell: 'Zoysia is far finer-bladed and stiff; St. Augustine is coarse, wide, and spongy' },
    ],
    bladeWidth: 'Broad (5-8mm)',
    texture: 'Coarse',
    growthHabit: 'Spreading (stolons)',
    bladeShape: 'Broad, flat, rounded tips',
    color: 'Dark green',
    density: 'Dense, coarse',
    rootSystem: 'Stolons',
    season: 'Warm',
    wateringNeeds: {
      weeklyRequirements: {
        spring: 0.8,
        summer: 1.0,
        fall: 0.8,
        winter: 0.0
      },
      rootDepth: 12,
      droughtTolerance: 'Medium',
      irrigationFrequency: {
        spring: '2-3 times per week',
        summer: '3-4 times per week',
        fall: '2-3 times per week',
        winter: 'Not needed (dormant)'
      },
      specialConsiderations: [
        'Moderate water requirements',
        'Good drought tolerance once established',
        'Dormant in winter'
      ]
    },
    droughtTolerance: 'Medium',
    shadeTolerance: 'High',
    trafficTolerance: 'Low',
    diseaseResistance: 'Medium',
    mowingHeight: '2.5-4 inches',
    mowingDetails: {
      optimal: '2.5-4.0 inches',
      minimum: '2.0 inches',
      maximum: '4.5 inches',
      frequency: {
        growingSeason: 'Every 5-7 days',
        peakGrowth: 'Every 4-5 days',
        dormantSeason: 'Not needed (dormant)'
      },
      tips: [
        'Never remove more than 1/3 of blade to avoid scalping',
        'Maintain 3.5-4 inches for best disease resistance',
        'Dwarf varieties (Seville, DelMar) can be mowed to 2-2.5 inches',
        'Use sharp blades - broad leaves show ragged cuts easily'
      ]
    },
    soilPreference: 'Wide range, salt tolerant',
    pHRange: '6.0-7.5',
    maintenanceLevel: 'Medium-High',
    coldTolerance: 'Low (dormant below 55°F, damage below 20°F)',
    establishmentTime: '14-21 days',
    peakGrowth: 'May-September',
    dormant: 'November-March',
    fertilization: 'Moderate (3-4x/year)',
    fertilizationNeeds: {
      annualNPK: {
        nitrogen: 3.0,
        phosphorus: 1.0,
        potassium: 2.0
      },
      seasonalSplit: {
        spring: 20,
        summer: 55,
        fall: 25,
        winter: 0
      },
      timing: {
        firstApplication: 'Late spring after green-up',
        lastApplication: 'Early fall before dormancy',
        frequency: 'Every 6-8 weeks during growing season'
      }
    },
    aerationTiming: {
      optimalSeasons: ['spring'],
      soilTemperature: 'When soil reaches 65-75°F',
      frequency: 'Once per year in late spring',
      depth: '1-2 inches',
      timing: {
        spring: 'Late spring after dormancy breaks',
        fall: 'Not recommended (approaching dormancy)'
      },
      considerations: [
        'Spring aeration only; stolon damage risk in fall',
        'Thick stolon layer benefits from annual aeration',
        'Avoid aerating during drought stress',
        'Follow with fertilization for best recovery'
      ]
    },
    overseeding: 'Not applicable - establish by sod, plugs, or sprigs'
  },

  'Centipede': {
    name: 'Centipede',
    photoIdCues: [
      'Distinctive pale apple-green / yellow-green color, even when healthy',
      'Medium-width blades with blunt, rounded tips on short upright stems',
      'Slender stolons creep along the surface, giving a centipede-like look at edges',
      'Slow-growing, low, even stand that rarely looks lush dark green',
    ],
    seedhead: 'Single slender spike-like raceme; occasionally seen between mowings',
    dormancyAppearance: 'Straw-brown after winter frost (stays greenish in mild Gulf Coast winters); iron-deficiency yellowing common in alkaline soils',
    lookalikes: [
      { grass: 'St. Augustinegrass', tell: 'St. Augustine has much wider blue-green blades and thick flattened stolons; centipede is narrower and apple-green' },
      { grass: 'Zoysiagrass', tell: 'Zoysia is deeper green, denser, and stiff to the touch; centipede is paler with blunt-tipped blades and an open habit' },
    ],
    bladeWidth: 'Medium (3-5mm)',
    texture: 'Coarse',
    growthHabit: 'Slow spreading (stolons)',
    bladeShape: 'Medium-width, pointed',
    color: 'Light green to yellow-green',
    density: 'Less dense, open',
    rootSystem: 'Shallow stolons',
    season: 'Warm',
    wateringNeeds: {
      weeklyRequirements: {
        spring: 0.8,
        summer: 1.0,
        fall: 0.5,
        winter: 0.0
      },
      rootDepth: 9,
      droughtTolerance: 'Low',
      irrigationFrequency: {
        spring: '1-2 times per week',
        summer: '2-3 times per week',
        fall: '1 time per week',
        winter: 'Not needed (dormant)'
      },
      specialConsiderations: [
        'Low water requirements match low maintenance profile',
        'Over-watering can encourage disease',
        'Dormant in winter, no irrigation needed',
        'Correct yellowing with chelated iron or iron sulfate, not extra nitrogen - especially on higher-pH soils',
        'Apply phosphorus only if a soil test shows a deficiency; excess N or P plus thatch drives centipedegrass decline'
      ]
    },
    droughtTolerance: 'Low',
    shadeTolerance: 'Medium',
    trafficTolerance: 'Low',
    diseaseResistance: 'Medium',
    mowingHeight: '1.5-2 inches',
    mowingDetails: {
      optimal: '1.5-2.0 inches',
      minimum: '1.0 inch',
      maximum: '2.5 inches',
      frequency: {
        growingSeason: 'Every 7-10 days',
        peakGrowth: 'Weekly',
        dormantSeason: 'Not needed (dormant)'
      },
      tips: [
        'Centipede grows slowly and needs less frequent mowing',
        'Never scalp centipede - it recovers very slowly',
        'Mow at 2 inches in partial shade',
        'Sharp blades are essential for clean cuts on fine texture'
      ]
    },
    soilPreference: 'Acidic, low fertility',
    pHRange: '5.0-6.0',
    maintenanceLevel: 'Very Low',
    coldTolerance: 'Low (dormant below 50°F, damage below 20°F)',
    establishmentTime: '28-42 days',
    peakGrowth: 'May-September',
    dormant: 'November-March',
    fertilization: 'Light (1-2x/year)',
    fertilizationNeeds: {
      annualNPK: {
        nitrogen: 1.0,
        phosphorus: 0,
        potassium: 1.0
      },
      seasonalSplit: {
        spring: 40,
        summer: 60,
        fall: 0,
        winter: 0
      },
      timing: {
        firstApplication: 'Late spring after full green-up',
        lastApplication: 'Mid-summer at latest',
        frequency: 'Once or twice per year maximum'
      }
    },
    aerationTiming: {
      optimalSeasons: ['spring'],
      soilTemperature: 'When soil reaches 65-70°F',
      frequency: 'Once every 1-2 years',
      depth: '1-2 inches',
      timing: {
        spring: 'Late spring after green-up',
        fall: 'Not recommended (approaching dormancy)'
      },
      considerations: [
        'Low-maintenance grass; aerate only when compacted',
        'Acidic soil preference means less compaction issues',
        'Slow growth means longer recovery after aeration',
        'Avoid over-aerating; once every 1-2 years is sufficient'
      ]
    },
    overseeding: 'Late spring',
    seedingRate: {
      newLbsPer1000Min: 0.25,
      newLbsPer1000Max: 0.5,
      overseedLbsPer1000Min: 0.25,
      overseedLbsPer1000Max: 0.5,
    },
  },

  'Bahiagrass': {
    name: 'Bahiagrass',
    photoIdCues: [
      'Open, thin, coarse stand — never a dense carpet',
      'Tall V-shaped seed stalks shoot up within days of mowing and dominate photos',
      'Shiny, coarse blades, light-to-medium green, often in clumpy patches',
      'Common in rural Southeast/Florida lawns and roadside-style turf',
    ],
    seedhead: 'Distinctive Y-shaped seedhead — two (sometimes three) slender spike branches atop a tall stalk; almost always visible days after mowing',
    dormancyAppearance: 'Tan-brown through winter after frost; also thins and browns in extended drought but recovers with rain',
    lookalikes: [
      { grass: 'Centipede', tell: 'Centipede forms a low, even, apple-green mat with no tall seed stalks; bahia is coarse and open with constant Y-shaped seedheads' },
      { grass: 'St. Augustinegrass', tell: 'St. Augustine is a dense blue-green carpet of wide blades; bahia is thinner, lighter, and dominated by tall seed stalks' },
    ],
    bladeWidth: 'Coarse (4-6mm)',
    texture: 'Rough',
    growthHabit: 'Bunch-type (clumping)',
    bladeShape: 'Coarse, rough',
    color: 'Light green to gray-green',
    density: 'Coarse, open',
    rootSystem: 'Deep fibrous (can reach 6+ feet)',
    season: 'Warm',
    wateringNeeds: {
      weeklyRequirements: {
        spring: 0.8,
        summer: 1.0,
        fall: 0.8,
        winter: 0.0
      },
      rootDepth: 84,
      droughtTolerance: 'Very High',
      irrigationFrequency: {
        spring: '1-2 times per week',
        summer: '1-2 times per week',
        fall: '1 time per week',
        winter: 'Not needed (dormant)'
      },
      specialConsiderations: [
        'Very deep root system (up to 7+ feet) provides exceptional drought tolerance',
        'One of the most drought-tolerant warm-season grasses',
        'Dormant in winter, no irrigation needed'
      ]
    },
    droughtTolerance: 'Very High',
    shadeTolerance: 'Low',
    trafficTolerance: 'Medium',
    diseaseResistance: 'High',
    mowingHeight: '3-4 inches',
    mowingDetails: {
      optimal: '3.0-4.0 inches',
      minimum: '2.5 inches',
      maximum: '4.5 inches',
      frequency: {
        growingSeason: 'Every 7-14 days',
        peakGrowth: 'Weekly',
        dormantSeason: 'Not needed (dormant)'
      },
      tips: [
        'Bahia grows slowly and needs less frequent mowing than bermuda',
        'Tall seed heads appear frequently - mow before they mature',
        'Use a sharp rotary mower - bahia is tough on blades',
        'Can tolerate infrequent mowing in low-maintenance settings'
      ]
    },
    soilPreference: 'Sandy, poor soils',
    pHRange: '5.0-6.5',
    maintenanceLevel: 'Very Low',
    coldTolerance: 'Medium (dormant below 55°F)',
    establishmentTime: '28-60 days',
    peakGrowth: 'May-September',
    dormant: 'November-March',
    fertilization: 'Light-Moderate (2-3x/year)',
    fertilizationNeeds: {
      annualNPK: {
        nitrogen: 2.0,
        phosphorus: 0.5,
        potassium: 1.0
      },
      seasonalSplit: {
        spring: 30,
        summer: 50,
        fall: 20,
        winter: 0
      },
      timing: {
        firstApplication: 'Late spring after green-up',
        lastApplication: 'Early fall',
        frequency: '2-3 applications per year'
      }
    },
    aerationTiming: {
      optimalSeasons: ['spring'],
      soilTemperature: 'When soil reaches 65-75°F',
      frequency: 'Once every 1-2 years',
      depth: '1-2 inches',
      timing: {
        spring: 'Late spring after green-up',
        fall: 'Not recommended (approaching dormancy)'
      },
      considerations: [
        'Deep root system reduces compaction issues',
        'Aerate only when soil is noticeably compacted',
        'Sandy soil preference means less frequent aeration needed',
        'Bunch-type growth recovers well from aeration'
      ]
    },
    overseeding: 'Late spring',
    seedingRate: {
      newLbsPer1000Min: 5,
      newLbsPer1000Max: 10,
      overseedLbsPer1000Min: 3,
      overseedLbsPer1000Max: 4,
    },
  },

  'Buffalograss': {
    name: 'Buffalograss',
    photoIdCues: [
      'Soft gray-green / sage color distinct from the true green of other lawns',
      'Fine, curly blades that arch over, giving a slightly shaggy, prairie look',
      'Thin, open, low-growing turf with fine stolons visible between plants',
      'Common in low-water Plains and Mountain West lawns',
    ],
    seedhead: 'Short flag-like male seedheads held just above the canopy; common in lightly mowed or unmowed patches',
    dormancyAppearance: 'Straw-tan much of the year — browns at first fall frost, greens up late spring, and browns again in severe summer drought (recovers with rain)',
    lookalikes: [
      { grass: 'Bermudagrass', tell: 'Bermuda is greener and denser with wiry runners and bird-foot seedheads; buffalograss is gray-green with fine curly blades and an open stand' },
      { grass: 'Fine Fescue', tell: 'Fine fescue is a deeper green cool-season bunch grass; buffalograss is sage-toned, low, and spreads with visible stolons' },
    ],
    bladeWidth: 'Fine (1-2mm)',
    texture: 'Soft',
    growthHabit: 'Spreading (stolons)',
    bladeShape: 'Fine, soft, curly',
    color: 'Blue-green to gray-green',
    density: 'Fine, soft',
    rootSystem: 'Deep stolons (native prairie, roots reach 4+ feet)',
    season: 'Warm',
    wateringNeeds: {
      weeklyRequirements: {
        spring: 0.25,
        summer: 0.5,
        fall: 0.25,
        winter: 0.0
      },
      rootDepth: 48,
      droughtTolerance: 'Very High',
      irrigationFrequency: {
        spring: 'Every 2-3 weeks',
        summer: 'Every 2-4 weeks',
        fall: 'Every 2-3 weeks',
        winter: 'Not needed (dormant)'
      },
      specialConsiderations: [
        'Extremely drought tolerant',
        'Native grass with minimal water needs',
        'Dormant in winter'
      ]
    },
    droughtTolerance: 'Very High',
    shadeTolerance: 'Low',
    trafficTolerance: 'Medium',
    diseaseResistance: 'High',
    mowingHeight: '2-4 inches (or unmowed)',
    mowingDetails: {
      optimal: '2.0-4.0 inches',
      minimum: '2.0 inches',
      maximum: '6.0 inches',
      frequency: {
        growingSeason: 'Every 10-14 days',
        peakGrowth: 'Every 7-10 days',
        dormantSeason: 'Not needed (dormant)'
      },
      tips: [
        'Buffalograss needs the least mowing of any lawn grass',
        'Can be left unmowed for a natural prairie look (4-6 inches)',
        'If mowing, maintain 2-3 inches for a manicured appearance',
        'Avoid mowing below 2 inches - buffalo recovers slowly from scalping'
      ]
    },
    soilPreference: 'Clay, alkaline soils',
    pHRange: '6.0-7.5',
    maintenanceLevel: 'Very Low',
    coldTolerance: 'High (dormant below 50°F, tolerates freezing)',
    establishmentTime: '21-35 days',
    peakGrowth: 'May-September',
    dormant: 'October-April',
    fertilization: 'Light (1-2x/year)',
    fertilizationNeeds: {
      annualNPK: {
        nitrogen: 1.0,
        phosphorus: 0.25,
        potassium: 0.5
      },
      seasonalSplit: {
        spring: 50,
        summer: 50,
        fall: 0,
        winter: 0
      },
      timing: {
        firstApplication: 'Late May to mid-June',
        lastApplication: 'Late July',
        frequency: 'Once or twice per year'
      }
    },
    aerationTiming: {
      optimalSeasons: ['spring'],
      soilTemperature: 'When soil reaches 65-75°F',
      frequency: 'Once every 2-3 years',
      depth: '1-2 inches',
      timing: {
        spring: 'Late spring after green-up',
        fall: 'Not recommended (approaching dormancy)'
      },
      considerations: [
        'Native grass rarely needs aeration',
        'Clay soil habitat may benefit from occasional aeration',
        'Very slow recovery; aerate sparingly',
        'Stolon-based growth can be disrupted by frequent aeration'
      ]
    },
    overseeding: 'Late spring',
    seedingRate: {
      newLbsPer1000Min: 3,
      newLbsPer1000Max: 5,
      overseedLbsPer1000Min: 2,
      overseedLbsPer1000Max: 3,
    },
  },

  'Kikuyugrass': {
    name: 'Kikuyugrass',
    photoIdCues: [
      'Light, bright apple-green color — noticeably lighter than bermuda',
      'Thick, flattened stems and coarse blades with fuzzy (hairy) leaf sheaths',
      'Aggressive thatchy runners climb over sidewalks, curbs, and flowerbed edges',
      'White thread-like stigma filaments can dust the lawn surface when flowering',
    ],
    seedhead: 'No raised seedhead — flowers hidden in the leaf sheaths, showing only as white thread-like stigma filaments scattered across the lawn surface',
    dormancyAppearance: 'Stays green nearly year-round in coastal California climates; browns only briefly after frost and recovers quickly',
    lookalikes: [
      { grass: 'Bermudagrass', tell: 'Bermuda is finer, darker or grayer green, with bird-foot seedheads; kikuyu is lighter green with thicker flattened stems and white stigma filaments' },
      { grass: 'St. Augustinegrass', tell: 'St. Augustine has wider blue-green blades and smooth sheaths; kikuyu is brighter green with hairy leaf sheaths and coarser runners' },
    ],
    bladeWidth: 'Medium-Coarse (4-6mm)',
    texture: 'Coarse, slightly hairy',
    growthHabit: 'Aggressive spreading (thick stolons and rhizomes)',
    bladeShape: 'Flat, folded in bud, blunt to boat-shaped tip on flattened stems',
    color: 'Light to medium green',
    density: 'Very dense, thatchy mat',
    rootSystem: 'Deep rhizomes plus thick surface stolons',
    season: 'Warm',
    wateringNeeds: {
      weeklyRequirements: {
        spring: 0.8,
        summer: 1.0,
        fall: 0.8,
        winter: 0.3
      },
      rootDepth: 24,
      droughtTolerance: 'Very High',
      irrigationFrequency: {
        spring: '1-2 times per week',
        summer: '1-2 times per week, deep soakings',
        fall: '1 time per week',
        winter: 'Rarely needed (coastal rainfall usually sufficient)'
      },
      specialConsiderations: [
        'Survives long dry spells by going semi-dormant, greens up fast after water',
        'Deep, infrequent watering discourages surface rooting and thatch',
        'Thrives in coastal Southern California with minimal irrigation'
      ]
    },
    droughtTolerance: 'Very High',
    shadeTolerance: 'Low',
    trafficTolerance: 'Very High',
    diseaseResistance: 'High',
    mowingHeight: '1-2 inches',
    mowingDetails: {
      optimal: '1.0-2.0 inches',
      minimum: '0.75 inches',
      maximum: '2.5 inches',
      frequency: {
        growingSeason: 'Weekly',
        peakGrowth: 'Every 4-5 days (extremely fast summer growth)',
        dormantSeason: 'Every 2-4 weeks or as needed'
      },
      tips: [
        'Mow low and often - kikuyu gets stemmy and scalps badly if left tall',
        'Rapid summer growth means missed mowings pile up fast',
        'Edge aggressively - stolons invade beds, sidewalks, and neighboring lawns',
        'Dethatch or verticut annually; kikuyu builds thatch quickly'
      ]
    },
    soilPreference: 'Adaptable; prefers fertile, well-drained soil',
    pHRange: '5.5-7.5',
    maintenanceLevel: 'Medium',
    coldTolerance: 'Low (browns with frost, dies back in hard freezes; best in coastal/mild-winter climates)',
    establishmentTime: '14-21 days (often spreads vegetatively without seeding)',
    peakGrowth: 'May-September',
    dormant: 'December-February (semi-dormant, stays partly green in frost-free coastal areas)',
    fertilization: 'Light-Moderate (2-3x/year)',
    fertilizationNeeds: {
      annualNPK: {
        nitrogen: 2.5,
        phosphorus: 0.5,
        potassium: 1.5
      },
      seasonalSplit: {
        spring: 40,
        summer: 40,
        fall: 20,
        winter: 0
      },
      timing: {
        firstApplication: 'Late spring after full green-up',
        lastApplication: 'Early fall (September)',
        frequency: 'Every 8-10 weeks during active growth; avoid overfeeding - it only accelerates an already aggressive grass'
      }
    },
    aerationTiming: {
      optimalSeasons: ['spring', 'summer'],
      soilTemperature: 'When soil reaches 65-75°F',
      frequency: 'Once per year',
      depth: '2-3 inches',
      timing: {
        spring: 'Late spring after green-up',
        fall: 'Not recommended (slowing growth)'
      },
      considerations: [
        'Pair aeration with dethatching - thatch is kikuyu\'s main problem',
        'Recovers from aeration extremely fast during warm weather',
        'Aggressive stolons quickly fill any open holes',
        'Avoid aerating while frost-browned or semi-dormant'
      ]
    },
    overseeding: 'Late spring (rarely needed - spreads aggressively on its own)',
    seedingRate: {
      newLbsPer1000Min: 1,
      newLbsPer1000Max: 2,
      overseedLbsPer1000Min: 0.5,
      overseedLbsPer1000Max: 1,
    },
  },

  'Annual Ryegrass': {
    name: 'Annual Ryegrass',
    photoIdCues: [
      'Light, bright green — noticeably paler than perennial ryegrass or fescue',
      'Coarser, glossy blades in fast-growing clumps; stand looks thin and uneven',
      'Common as a temporary winter overseed on dormant warm-season lawns (green grass over brown base)',
    ],
    seedhead: 'Slender spike similar to perennial ryegrass; awn treatment varies across references and is withheld from the Atlas model pending expert review',
    dormancyAppearance: 'Dies out completely in early-summer heat (true annual), leaving brown thinning patches rather than going dormant',
    lookalikes: [
      { grass: 'Perennial Ryegrass', tell: 'Perennial rye is darker green and finer; annual rye is pale, coarse, and clumpy' },
      { grass: 'Tall Fescue', tell: 'Tall fescue is a darker, denser, permanent bunch grass; annual rye is paler with a shinier blade' },
    ],
    bladeWidth: 'Medium (3-6mm)',
    texture: 'Smooth, glossy underside',
    growthHabit: 'Bunch-type (clumping)',
    bladeShape: 'Flat with prominent veins, pointed tip, shiny back of blade',
    color: 'Light to bright green',
    density: 'Moderate, clumpier and more open than perennial rye',
    rootSystem: 'Shallow fibrous roots',
    season: 'Cool',
    wateringNeeds: {
      weeklyRequirements: {
        spring: 1.0,
        summer: 1.5,
        fall: 1.0,
        winter: 0.5
      },
      rootDepth: 8,
      droughtTolerance: 'Low',
      irrigationFrequency: {
        spring: '2-3 times per week',
        summer: '3-4 times per week (if surviving at all)',
        fall: 'Light, frequent watering during overseed establishment',
        winter: '1 time per week in dry southern winters'
      },
      specialConsiderations: [
        'Shallow roots need consistent moisture, especially during germination',
        'As a winter overseed, water lightly and daily for the first 7-10 days after seeding',
        'Expect it to die out with summer heat in the South - that is by design for overseeding'
      ]
    },
    droughtTolerance: 'Low',
    shadeTolerance: 'Medium',
    trafficTolerance: 'Medium',
    diseaseResistance: 'Low',
    mowingHeight: '1.5-2.5 inches',
    mowingDetails: {
      optimal: '1.5-2.5 inches',
      minimum: '1 inch',
      maximum: '3 inches',
      frequency: {
        growingSeason: 'Weekly',
        peakGrowth: 'Every 5-7 days (grows very fast in cool weather)',
        dormantSeason: 'As needed'
      },
      tips: [
        'When overseeded on bermuda, mow slightly lower in late spring to help the base grass transition back',
        'Fast vertical growth means it looks shaggy quickly - keep up with mowing',
        'Sharp blades matter; annual rye shreds with dull blades',
        'First mow ~3 weeks after overseeding, once seedlings hit 2-3 inches'
      ]
    },
    soilPreference: 'Adaptable; prefers moist, fertile soil',
    pHRange: '6.0-7.0',
    maintenanceLevel: 'Medium',
    coldTolerance: 'Low-Medium (less winter-hardy than perennial rye; can winter-kill in the far North)',
    establishmentTime: '5-10 days (fastest-germinating common lawn grass)',
    peakGrowth: 'October-December, February-April (as a southern winter overseed)',
    dormant: 'Dies out in summer heat in the South; behaves as a true annual',
    fertilization: 'Light (1-2x per overseed season)',
    fertilizationNeeds: {
      annualNPK: {
        nitrogen: 1.5,
        phosphorus: 0.5,
        potassium: 1.0
      },
      seasonalSplit: {
        spring: 30,
        summer: 0,
        fall: 50,
        winter: 20
      },
      timing: {
        firstApplication: '3-4 weeks after fall overseeding, once mowed twice',
        lastApplication: 'Late winter (February); stop feeding so the base warm-season grass can transition back',
        frequency: 'Every 6-8 weeks at 0.5 lb N/1,000 sq ft during the cool season'
      }
    },
    aerationTiming: {
      optimalSeasons: ['fall'],
      soilTemperature: 'When soil reaches 55-65°F',
      frequency: 'Not typically aerated for its own sake (short-lived annual)',
      depth: '2-3 inches',
      timing: {
        spring: 'Not recommended (grass is dying out)',
        fall: 'Aerate the base lawn just before overseeding for better seed-soil contact'
      },
      considerations: [
        'Aeration decisions should follow the permanent base grass, not the annual overseed',
        'Core aeration right before fall overseeding improves germination',
        'Avoid disturbing seedlings in the first 4-6 weeks',
        'Short-lived roots gain little from standalone aeration'
      ]
    },
    overseeding: 'Early-to-mid fall (soil temps 55-70°F) over dormant warm-season lawns',
    seedingRate: {
      newLbsPer1000Min: 8,
      newLbsPer1000Max: 10,
      overseedLbsPer1000Min: 5,
      overseedLbsPer1000Max: 10,
    },
  },

  'Mixed Cool-Season Blend': {
    name: 'Mixed Cool-Season Blend',
    photoIdCues: [
      'Non-uniform texture: coarse tall fescue clumps mixed among finer bluegrass/ryegrass blades',
      'Multiple shades of green in one frame rather than a single uniform tone',
      'Patchwork sheen — glossy ryegrass patches beside duller bluegrass or fescue',
      'Typical of northern lawns seeded from a mixed bag; no single texture dominates',
    ],
    seedhead: 'Varies by component species; rarely seen in mowed lawns',
    dormancyAppearance: 'Browns unevenly under summer stress — component species brown at different rates, giving a mottled green-and-tan look',
    lookalikes: [
      { grass: 'Tall Fescue', tell: 'A pure tall fescue lawn is uniformly coarse; a blend shows visibly mixed blade widths and greens' },
      { grass: 'Kentucky Bluegrass', tell: 'A pure KBG lawn is a single fine, uniform dark blue-green carpet; a blend shows texture and color variation' },
    ],
    bladeWidth: 'Varies (2-8mm) - a mix of fine and wide blades',
    texture: 'Mixed - smooth fine blades interspersed with coarser fescue blades',
    growthHabit: 'Mixed - spreading (KBG rhizomes) plus bunch-type (rye/fescue) patches',
    bladeShape: 'Non-uniform: boat-tipped fine blades, glossy pointed blades, and flat ribbed blades in one stand',
    color: 'Medium to dark green, often subtly mottled between patches',
    density: 'Dense overall with visible texture variation',
    rootSystem: 'Mixed - shallow rhizomes plus deeper fibrous bunch roots',
    season: 'Cool',
    wateringNeeds: {
      weeklyRequirements: {
        spring: 1.0,
        summer: 1.2,
        fall: 1.0,
        winter: 0.3
      },
      rootDepth: 18,
      droughtTolerance: 'Medium',
      irrigationFrequency: {
        spring: '2 times per week',
        summer: '2-3 times per week',
        fall: '1-2 times per week',
        winter: '1 time per week (if needed)'
      },
      specialConsiderations: [
        'Water to the thirstiest component (usually the bluegrass/rye fraction)',
        'Deep, infrequent watering favors the deeper-rooted fescue fraction',
        'Blends self-balance over time - the best-adapted species dominates each microclimate'
      ]
    },
    droughtTolerance: 'Medium',
    shadeTolerance: 'Medium',
    trafficTolerance: 'High',
    diseaseResistance: 'High',
    mowingHeight: '2.5-3.5 inches',
    mowingDetails: {
      optimal: '2.5-3.5 inches',
      minimum: '2 inches',
      maximum: '4 inches',
      frequency: {
        growingSeason: 'Weekly',
        peakGrowth: 'Every 5-7 days',
        dormantSeason: 'Bi-weekly or as needed'
      },
      tips: [
        'Mow at a height that suits all components - 3 inches is the safe middle ground',
        'Never remove more than 1/3 of the blade at once',
        'Raise to 3.5-4 inches during summer heat',
        'A single consistent height keeps the mixed textures reading as one lawn'
      ]
    },
    soilPreference: 'Adaptable; well-drained loam ideal',
    pHRange: '6.0-7.0',
    maintenanceLevel: 'Medium',
    coldTolerance: 'High (blend components are all winter-hardy cool-season grasses)',
    establishmentTime: '7-21 days (rye up first, bluegrass fills in later)',
    peakGrowth: 'March-May, September-November',
    dormant: 'December-February (winter); partial summer dormancy in heat without irrigation',
    fertilization: 'Moderate (3x/year)',
    fertilizationNeeds: {
      annualNPK: {
        nitrogen: 3.0,
        phosphorus: 1.0,
        potassium: 2.0
      },
      seasonalSplit: {
        spring: 30,
        summer: 0,
        fall: 70,
        winter: 0
      },
      timing: {
        firstApplication: 'Early spring when growth begins',
        lastApplication: 'Late fall (November) for root reserves',
        frequency: 'Every 6-8 weeks during the growing season, fall-weighted'
      }
    },
    aerationTiming: {
      optimalSeasons: ['fall'],
      soilTemperature: 'When soil reaches 55-65°F',
      frequency: 'Once per year, fall preferred',
      depth: '2-3 inches',
      timing: {
        spring: 'Early spring if needed (not preferred)',
        fall: 'Early fall for best results'
      },
      considerations: [
        'Fall aeration plus overseeding keeps the blend balanced',
        'Overseed with a matching sun/shade blend, not a single species',
        'Aerate when soil is moist but not wet',
        'Avoid aeration during summer heat stress'
      ]
    },
    overseeding: 'Early fall',
    seedingRate: {
      newLbsPer1000Min: 4,
      newLbsPer1000Max: 6,
      overseedLbsPer1000Min: 2,
      overseedLbsPer1000Max: 3,
    },
  },
  Carpetgrass: CARPETGRASS,
}

// Common model-output variants → canonical GRASS_CHARACTERISTICS keys. The
// Vision prompt asks for canonical names, but the model often returns shorthand
// ("Bermuda", "St Augustine", "Zoysia"). Without this, getGrassCharacteristics
// returns null and a confident identification silently ships with NO
// characteristics / compatibility / seasonal enrichment (the core of the result
// card). NOTE: this is intentionally NOT mapGrassTypeToLawnProfile — that maps
// to lawn-profile dropdown values ('Bermuda Grass', 'Fescue') which are not
// GRASS_CHARACTERISTICS keys and would collapse Tall/Fine Fescue. The bare
// 'Fescue' bucket resolves to Tall Fescue (the dominant lawn fescue) so a Fescue
// lawn gets a real plan instead of the generic fallback; the ambiguity is still
// surfaced via normalizeGrassType (ambiguous: true) so the user confirms Tall vs Fine.
const GRASS_NAME_ALIASES: Record<string, string> = {
  carpetgrass: 'Carpetgrass',
  'carpet grass': 'Carpetgrass',
  'common carpetgrass': 'Carpetgrass',
  'axonopus fissifolius': 'Carpetgrass',
  'axonopus affinis': 'Carpetgrass',
  bermuda: 'Bermudagrass',
  'bermuda grass': 'Bermudagrass',
  zoysia: 'Zoysiagrass',
  'zoysia grass': 'Zoysiagrass',
  'st. augustine': 'St. Augustinegrass',
  'st augustine': 'St. Augustinegrass',
  'saint augustine': 'St. Augustinegrass',
  'st. augustine grass': 'St. Augustinegrass',
  'st augustine grass': 'St. Augustinegrass',
  'centipede grass': 'Centipede',
  centipedegrass: 'Centipede',
  bahia: 'Bahiagrass',
  'bahia grass': 'Bahiagrass',
  buffalo: 'Buffalograss',
  'buffalo grass': 'Buffalograss',
  'kentucky blue': 'Kentucky Bluegrass',
  kbg: 'Kentucky Bluegrass',
  bluegrass: 'Kentucky Bluegrass',
  'perennial rye': 'Perennial Ryegrass',
  ryegrass: 'Perennial Ryegrass',
  rye: 'Perennial Ryegrass',
  // Bare 'Fescue' bucket → dominant lawn fescue (Tall). normalizeGrassType still
  // flags it ambiguous so the user is nudged to confirm Tall vs Fine.
  fescue: 'Tall Fescue',
  'fescue grass': 'Tall Fescue',
  kikuyu: 'Kikuyugrass',
  'kikuyu grass': 'Kikuyugrass',
  'pennisetum clandestinum': 'Kikuyugrass',
  'cenchrus clandestinus': 'Kikuyugrass',
  'annual rye': 'Annual Ryegrass',
  'annual rye grass': 'Annual Ryegrass',
  'italian ryegrass': 'Annual Ryegrass',
  'italian rye': 'Annual Ryegrass',
  'winter rye': 'Annual Ryegrass',
  'winter ryegrass': 'Annual Ryegrass',
  'cool-season mix': 'Mixed Cool-Season Blend',
  'cool season mix': 'Mixed Cool-Season Blend',
  'cool-season blend': 'Mixed Cool-Season Blend',
  'cool season blend': 'Mixed Cool-Season Blend',
  'mixed cool-season': 'Mixed Cool-Season Blend',
  'mixed cool season blend': 'Mixed Cool-Season Blend',
  'kbg/rye/fescue mix': 'Mixed Cool-Season Blend',
  'cool-season lawn mix': 'Mixed Cool-Season Blend',
}

// Resolve a model-output grass name to its canonical GRASS_CHARACTERISTICS key
// ("Bermuda" → "Bermudagrass"). Returns the input unchanged when it's already
// canonical or unknown, so callers can pass the result straight to name-keyed
// lookups (guide slugs, reference photos) without a null branch.
export function resolveGrassDisplayName(grassType: string): string {
  if (!grassType) return grassType
  if (GRASS_CHARACTERISTICS[grassType]) return grassType
  return GRASS_NAME_ALIASES[grassType.trim().toLowerCase()] ?? grassType
}

// Helper function to get grass characteristics by name. Resolves common
// model-output variants to the canonical key (exact match takes the fast path).
export function getGrassCharacteristics(grassType: string): GrassCharacteristics | null {
  if (!grassType) return null
  if (GRASS_CHARACTERISTICS[grassType]) return GRASS_CHARACTERISTICS[grassType]
  const canonical = GRASS_NAME_ALIASES[grassType.trim().toLowerCase()]
  return canonical ? (GRASS_CHARACTERISTICS[canonical] ?? null) : null
}

// Helper function to get all cool-season grasses
export function getCoolSeasonGrasses(): GrassCharacteristics[] {
  return Object.values(GRASS_CHARACTERISTICS).filter(grass => grass.season === 'Cool')
}

// Helper function to get all warm-season grasses
export function getWarmSeasonGrasses(): GrassCharacteristics[] {
  return Object.values(GRASS_CHARACTERISTICS).filter(grass => grass.season === 'Warm')
}

// Helper function to get grass types by maintenance level
export function getGrassesByMaintenanceLevel(level: string): GrassCharacteristics[] {
  return Object.values(GRASS_CHARACTERISTICS).filter(grass => grass.maintenanceLevel === level)
}

// Helper function to get grass types by drought tolerance
export function getGrassesByDroughtTolerance(tolerance: string): GrassCharacteristics[] {
  return Object.values(GRASS_CHARACTERISTICS).filter(grass => grass.droughtTolerance === tolerance)
}
